import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatTime, formatDate, apiError, relativeShort } from "../lib/format";
import { toast } from "sonner";
import { PaperPlaneTilt, ChatCircleDots, ArrowLeft, ArrowUpRight, Calculator, MagnifyingGlass, X as XIcon } from "@phosphor-icons/react";
import { AttachButton, PendingAttachment, MessageAttachment } from "../lib/chat";
import QuickReplies from "../components/QuickReplies";
import { TagsList, TagPicker } from "../components/Tags";
import ChatSimulator from "../components/ChatSimulator";
import { PencilSimple, Trash, Check } from "@phosphor-icons/react";

// Merge new messages by id — preserves optimistic appends, adds new ones.
// Pure helper kept at module scope so it is referentially stable across renders.
const mergeMessages = (prev, incoming) => {
  const seen = new Map();
  for (const m of prev) seen.set(m.id, m);
  for (const m of incoming) seen.set(m.id, m);
  const arr = Array.from(seen.values());
  arr.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  return arr;
};

export default function ChatPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [pending, setPending] = useState(null);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(null); // {id, text}
  const [qrOpen, setQrOpen] = useState(false);
  const [qrQuery, setQrQuery] = useState("");
  const [tagCatalog, setTagCatalog] = useState([]);
  const [simOpen, setSimOpen] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [filterTagIds, setFilterTagIds] = useState([]);
  const scrollRef = useRef(null);
  const stickBottomRef = useRef(true);
  const inputRef = useRef(null);

  // Load tag catalog once for rendering tag chips
  useEffect(() => {
    if (user?.role === "client") return;
    api.get("/tags").then((r) => setTagCatalog(r.data)).catch(() => {});
  }, [user?.role]);

  // Track whether user is near bottom of the thread (within 80px).
  const isNearBottom = () => {
    const el = scrollRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/conversations");
      // Backend already sorts by last_message_at desc — use as-is (most recent first)
      setConvs(data);
      // Preserve current active selection across polls (functional setter reads latest state)
      setActive((prev) => {
        // 1) If we already have an active conversation, keep it (refreshed with latest server data)
        if (prev) {
          const fresh = data.find((c) => c.id === prev.id);
          return fresh || prev;
        }
        // 2) Honor legacy `location.state.focusConvId` from notifications card
        const focusId = location.state?.focusConvId;
        if (focusId) {
          const found = data.find((c) => c.id === focusId);
          if (found) {
            navigate(location.pathname, { replace: true, state: {} });
            // Also persist in URL so reloads keep it
            setSearchParams({ conv: found.id }, { replace: true });
            return found;
          }
        }
        // 3) Honor URL ?conv=<id> (survives reloads)
        const urlId = searchParams.get("conv");
        if (urlId) {
          const found = data.find((c) => c.id === urlId);
          if (found) return found;
        }
        // 4) First load fallback: most recent conversation
        return data[0] || null;
      });
    } catch {}
  }, [location.state, location.pathname, navigate, searchParams, setSearchParams]);

  useEffect(() => {
    loadConvs();
    const i = setInterval(loadConvs, 3000);
    return () => clearInterval(i);
  }, [loadConvs]);

  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 30;

  const activeId = active?.id;
  useEffect(() => {
    if (!activeId) return;
    const convId = activeId;
    let cancelled = false;
    let firstLoad = true;
    // Latest message timestamp seen — used for incremental polling
    let lastSeenISO = null;

    const initial = async () => {
      try {
        const { data } = await api.get(
          `/chat/conversations/${convId}/messages?limit=${PAGE_SIZE}`
        );
        if (cancelled) return;
        const arr = (data.messages || []).filter((m) => m.conversation_id === convId);
        setHasMore(!!data.has_more);
        setMessages(arr);
        if (arr.length) lastSeenISO = arr[arr.length - 1].created_at;
        setTimeout(scrollToBottom, 30);
        firstLoad = false;
      } catch {}
    };

    const poll = async () => {
      if (firstLoad) return; // initial still in flight
      try {
        const url = lastSeenISO
          ? `/chat/conversations/${convId}/messages?after=${encodeURIComponent(lastSeenISO)}`
          : `/chat/conversations/${convId}/messages?limit=${PAGE_SIZE}`;
        const { data } = await api.get(url);
        if (cancelled) return;
        const incoming = (data.messages || []).filter((m) => m.conversation_id === convId);
        if (incoming.length === 0) return;
        const wasNear = stickBottomRef.current;
        setMessages((prev) => {
          const prevForConv = prev.filter((m) => m.conversation_id === convId);
          const merged = mergeMessages(prevForConv, incoming);
          if (merged.length > prevForConv.length && wasNear) {
            setTimeout(scrollToBottom, 30);
          }
          return merged;
        });
        const newest = incoming[incoming.length - 1];
        if (!lastSeenISO || new Date(newest.created_at) > new Date(lastSeenISO)) {
          lastSeenISO = newest.created_at;
        }
      } catch {}
    };

    setMessages([]);
    setHasMore(false);
    stickBottomRef.current = true;
    initial();
    const i = setInterval(poll, 2000);
    return () => {
      cancelled = true;
      clearInterval(i);
    };
  }, [activeId, scrollToBottom]);

  const loadOlder = async () => {
    if (!active || loadingMore || !hasMore || messages.length === 0) return;
    setLoadingMore(true);
    const convId = active.id;
    const oldest = messages[0];
    const scrollEl = scrollRef.current;
    const beforeHeight = scrollEl ? scrollEl.scrollHeight : 0;
    const beforeTop = scrollEl ? scrollEl.scrollTop : 0;
    try {
      const { data } = await api.get(
        `/chat/conversations/${convId}/messages?before=${encodeURIComponent(oldest.created_at)}&limit=${PAGE_SIZE}`
      );
      const older = (data.messages || []).filter((m) => m.conversation_id === convId);
      setHasMore(!!data.has_more);
      if (older.length > 0) {
        setMessages((prev) => mergeMessages(older, prev));
        // Preserve viewport: keep distance from the bottom stable
        setTimeout(() => {
          if (!scrollEl) return;
          const newHeight = scrollEl.scrollHeight;
          scrollEl.scrollTop = beforeTop + (newHeight - beforeHeight);
        }, 30);
      }
    } catch {}
    finally {
      setLoadingMore(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if ((!text.trim() && !pending) || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chat/conversations/${active.id}/messages`, {
        text: text.trim(),
        attachment_id: pending?.id || null,
      });
      setMessages((m) => [...m, data]);
      setText("");
      setPending(null);
      setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 50);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const clientId = active ? (active.partner?.id || active.client?.id) : null;
  const partner = active ? (active.partner || active.client || active.advisor) : null;
  const canOpenProfile = user?.role === "advisor" && partner?.role === "client" && clientId;

  const openClientProfile = () => {
    if (canOpenProfile) navigate(`/app/clientes/${clientId}`);
  };

  // Click on a conversation in the list: make it active AND sync the URL so refresh keeps it.
  const selectConv = (c) => {
    setActive(c);
    setSearchParams({ conv: c.id }, { replace: true });
  };

  // Filtered list (text + tag) — advisor/admin only. Client always sees their single conv.
  const norm = (s) => (s || "").toString().toLowerCase().trim();
  const filteredConvs = useMemo(() => {
    if (user?.role === "client") return convs;
    const q = norm(filterText);
    const tagSet = new Set(filterTagIds);
    return convs.filter((c) => {
      const p = c.partner || c.client || c.advisor || {};
      if (tagSet.size > 0) {
        const ptags = p.tags || [];
        // Must include all selected tags (AND)
        for (const tid of tagSet) {
          if (!ptags.includes(tid)) return false;
        }
      }
      if (!q) return true;
      const haystack = [
        p.name,
        p.rut,
        p.phone,
        p.email,
      ]
        .map(norm)
        .join(" • ");
      return haystack.includes(q);
    });
  }, [convs, filterText, filterTagIds, user?.role]);

  const toggleTagFilter = (tid) => {
    setFilterTagIds((prev) => (prev.includes(tid) ? prev.filter((t) => t !== tid) : [...prev, tid]));
  };
  const clearFilters = () => {
    setFilterText("");
    setFilterTagIds([]);
  };
  const hasActiveFilter = filterText || filterTagIds.length > 0;

  const onTextChange = (e) => {
    const v = e.target.value;
    setText(v);
    // Detect slash command for quick replies (only advisor/admin).
    if (user.role === "advisor" || user.role === "admin") {
      const lastSlash = v.lastIndexOf("/");
      if (lastSlash >= 0) {
        const before = lastSlash === 0 ? "" : v[lastSlash - 1];
        const after = v.slice(lastSlash + 1);
        const atStart = lastSlash === 0 || /\s/.test(before);
        const hasNoSpaceAfter = !after.includes(" ") && !after.includes("\n");
        if (atStart && hasNoSpaceAfter) {
          setQrQuery(after);
          setQrOpen(true);
          return;
        }
      }
      setQrOpen(false);
    }
  };

  const pickQuickReply = (replyText) => {
    const v = text;
    const lastSlash = v.lastIndexOf("/");
    const before = lastSlash >= 0 ? v.slice(0, lastSlash) : v;
    setText(before + replyText);
    setQrOpen(false);
    setQrQuery("");
    setTimeout(() => inputRef.current?.focus(), 20);
  };

  const startEdit = (m) => setEditing({ id: m.id, text: m.text });
  const cancelEdit = () => setEditing(null);
  const saveEdit = async () => {
    if (!editing?.text?.trim()) return;
    try {
      const { data } = await api.patch(
        `/chat/conversations/${active.id}/messages/${editing.id}`,
        { text: editing.text.trim() }
      );
      setMessages((prev) => prev.map((m) => (m.id === data.id ? data : m)));
      setEditing(null);
      toast.success("Mensaje editado");
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteMsg = async (m) => {
    if (!window.confirm("¿Eliminar este mensaje? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/chat/conversations/${active.id}/messages/${m.id}`);
      setMessages((prev) => prev.map((x) => (x.id === m.id ? { ...x, deleted_at: new Date().toISOString(), text: "", attachment: null } : x)));
      toast.success("Mensaje eliminado");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="flex flex-col h-[calc(100dvh-7rem)] md:h-[calc(100dvh-8rem)] lg:h-[calc(100dvh-10rem)]" data-testid="chat-page">
      <div className="bento flex-1 min-h-0 grid md:grid-cols-3 lg:grid-cols-4 overflow-hidden">
        {/* list */}
        <div className={`${active && "hidden md:block"} md:col-span-1 border-r border-hairline overflow-auto min-h-0 flex flex-col`}>
          <div className="px-4 py-4 border-b border-hairline flex-shrink-0 bg-surface sticky top-0 z-10">
            <div className="label-mono mb-0.5">Chat interno · Asesor</div>
            <div className="font-display font-black text-lg tracking-tight">
              {user.role === "client" ? "Tu asesor" : user.role === "advisor" ? "Conversaciones" : "Chats"}
              <span className="text-inkMuted font-mono font-medium text-sm ml-2">
                · {user.role === "client" ? convs.length : `${filteredConvs.length}/${convs.length}`}
              </span>
            </div>
            {/* Filtros (asesor/admin) */}
            {(user.role === "advisor" || user.role === "admin") && convs.length > 0 && (
              <div className="mt-3 space-y-2" data-testid="chat-filters">
                <div className="relative">
                  <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-inkMuted pointer-events-none" />
                  <input
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    placeholder="Nombre, RUT, teléfono o email…"
                    className="w-full h-9 pl-8 pr-8 text-xs border border-hairline bg-bg focus:outline-none focus:border-forest"
                    data-testid="chat-filter-text"
                  />
                  {filterText && (
                    <button
                      onClick={() => setFilterText("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-inkMuted hover:text-danger"
                      data-testid="chat-filter-clear-text"
                      aria-label="Limpiar"
                    >
                      <XIcon size={12} weight="bold" />
                    </button>
                  )}
                </div>
                {tagCatalog.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-inkMuted mr-1">Etiquetas:</span>
                    {tagCatalog.map((t) => {
                      const active = filterTagIds.includes(t.id);
                      return (
                        <button
                          key={t.id}
                          onClick={() => toggleTagFilter(t.id)}
                          data-testid={`chat-filter-tag-${t.id}`}
                          className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border transition-colors ${
                            active
                              ? "bg-forest text-bg border-forest"
                              : "border-hairline text-inkMuted hover:border-forest hover:text-forest"
                          }`}
                        >
                          {t.name}
                        </button>
                      );
                    })}
                    {hasActiveFilter && (
                      <button
                        onClick={clearFilters}
                        className="ml-auto text-[10px] font-mono uppercase tracking-widest text-burnt hover:underline"
                        data-testid="chat-filter-clear-all"
                      >
                        Limpiar
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          {convs.length === 0 && <div className="p-6 text-sm text-inkMuted">Sin conversaciones.</div>}
          {convs.length > 0 && filteredConvs.length === 0 && (
            <div className="p-6 text-sm text-inkMuted text-center" data-testid="chat-filter-empty">
              Sin resultados para los filtros aplicados.
              <button onClick={clearFilters} className="block mt-2 mx-auto text-xs font-mono uppercase tracking-widest text-burnt hover:underline">
                Limpiar filtros
              </button>
            </div>
          )}
          {filteredConvs.map((c) => {
            const p = c.partner || c.client || c.advisor;
            const isActive = active?.id === c.id;
            const tagIds = p?.tags || [];
            const last = c.last_message;
            const lastIsMine = last && last.sender_id === user.id;
            const lastTimeLabel = last ? relativeShort(last.created_at) : "";
            return (
              <button
                key={c.id}
                onClick={() => selectConv(c)}
                data-testid={`conv-${c.id}`}
                className={`w-full text-left px-4 py-3 border-b border-hairline hover:bg-bg ${isActive ? "bg-forest/5 border-l-2 border-l-forest" : ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    {tagIds.length > 0 && tagCatalog.length > 0 && (
                      <div className="mb-1">
                        <TagsList tagIds={tagIds} catalog={tagCatalog} size="xs" />
                      </div>
                    )}
                    <div className="font-semibold text-sm truncate">{p?.name || "—"}</div>
                    {last ? (
                      <div className={`mt-0.5 text-xs truncate ${c.unread > 0 && !lastIsMine ? "text-ink font-medium" : "text-inkMuted"}`}>
                        {lastIsMine && <span className="text-inkMuted">Tú: </span>}
                        {last.text || "—"}
                      </div>
                    ) : (
                      <div className="label-mono mt-1">{p?.role}</div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {lastTimeLabel && (
                      <span className={`font-mono text-[10px] ${c.unread > 0 ? "text-burnt font-semibold" : "text-inkMuted"}`}>
                        {lastTimeLabel}
                      </span>
                    )}
                    {c.unread > 0 && (
                      <span className="bg-burnt text-bg text-xs font-mono px-2 py-0.5 leading-none">
                        {c.unread}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* thread */}
        <div className={`${!active && "hidden md:flex"} md:col-span-2 lg:col-span-3 flex flex-col min-h-0 min-w-0 overflow-hidden`}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center flex-col text-inkMuted">
              <ChatCircleDots size={48} weight="duotone" />
              <div className="mt-2 text-sm">Selecciona una conversación</div>
            </div>
          ) : (
            <>
              <div className="h-auto min-h-[4rem] flex-shrink-0 border-b border-hairline px-5 py-2 flex items-start gap-3 flex-wrap">
                <button className="md:hidden p-2" onClick={() => setActive(null)}><ArrowLeft size={18} /></button>
                <div className="w-10 h-10 bg-forest text-bg flex items-center justify-center font-display font-bold flex-shrink-0">
                  {(partner?.name || "?")[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  {canOpenProfile ? (
                    <button
                      onClick={openClientProfile}
                      data-testid="chat-open-client-profile"
                      className="text-left group"
                      title="Ver perfil del cliente"
                    >
                      <div className="font-display font-bold underline decoration-2 decoration-forest/30 underline-offset-4 group-hover:decoration-burnt group-hover:text-burnt transition-colors flex items-center gap-1">
                        {partner?.name}
                        <ArrowUpRight size={14} weight="bold" className="opacity-60 group-hover:opacity-100" />
                      </div>
                      <div className="label-mono">{partner?.role} · ver perfil</div>
                    </button>
                  ) : (
                    <div>
                      <div className="font-display font-bold">{partner?.name}</div>
                      <div className="label-mono">{partner?.role}</div>
                    </div>
                  )}
                </div>
                {/* Tags: advisor/admin can view & manage client tags from the chat header */}
                {(user.role === "advisor" || user.role === "admin") && partner?.role === "client" && clientId && (
                  <div className="flex-shrink-0 flex items-center gap-2">
                    <TagPicker
                      userId={clientId}
                      currentTags={partner?.tags || []}
                      onChange={(next) => {
                        // Update local partner data optimistically
                        setActive((prev) => prev ? { ...prev, partner: { ...prev.partner, tags: next } } : prev);
                      }}
                      compact
                    />
                    <button
                      onClick={() => setSimOpen(true)}
                      title="Simular crédito y enviar al chat"
                      aria-label="Simulador de crédito"
                      data-testid="chat-open-simulator"
                      className="h-7 w-7 border border-hairline hover:bg-forest hover:text-bg hover:border-forest flex items-center justify-center transition-colors"
                    >
                      <Calculator size={13} weight="duotone" />
                    </button>
                  </div>
                )}
              </div>

              <div
                ref={scrollRef}
                onScroll={() => { stickBottomRef.current = isNearBottom(); }}
                className="flex-1 overflow-auto p-6 space-y-1 bg-bg"
                data-testid="chat-scroll"
              >
                {hasMore && (
                  <div className="flex justify-center pb-4">
                    <button
                      onClick={loadOlder}
                      disabled={loadingMore}
                      data-testid="chat-load-older"
                      className="h-9 px-4 border border-hairline bg-surface text-xs font-mono uppercase tracking-widest hover:bg-forest hover:text-bg hover:border-forest disabled:opacity-50 disabled:cursor-wait"
                    >
                      {loadingMore ? "Cargando…" : "↑ Cargar mensajes anteriores"}
                    </button>
                  </div>
                )}
                {messages.length === 0 && <div className="text-center text-inkMuted text-sm">Envía el primer mensaje.</div>}
                {messages.map((m, i) => {
                  const mine = m.sender_id === user.id;
                  const isDeleted = !!m.deleted_at;
                  const isEditingThis = editing?.id === m.id;
                  const prev = messages[i - 1];
                  const showDate = !prev || formatDate(prev.created_at) !== formatDate(m.created_at);
                  return (
                    <React.Fragment key={m.id}>
                      {showDate && (
                        <div className="text-center label-mono my-4">{formatDate(m.created_at)}</div>
                      )}
                      <div className={`flex ${mine ? "justify-end" : "justify-start"} group`} data-testid={`chat-msg-${m.id}`}>
                        <div className={`max-w-[75%] px-4 py-2 space-y-2 ${isDeleted ? "bg-bg border border-dashed border-hairline text-inkMuted italic" : mine ? "bg-forest text-bg" : "bg-surface border border-hairline"}`}>
                          {isDeleted ? (
                            <div className="text-sm">Mensaje eliminado</div>
                          ) : isEditingThis ? (
                            <div className="space-y-2">
                              <textarea
                                value={editing.text}
                                onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                                rows={Math.min(6, Math.max(2, editing.text.split("\n").length))}
                                className="w-full min-w-[240px] text-sm bg-surface text-ink border border-hairline p-2 resize-none whitespace-pre-wrap"
                                data-testid={`chat-edit-input-${m.id}`}
                                autoFocus
                              />
                              <div className="flex gap-2 justify-end">
                                <button onClick={cancelEdit} className="h-7 px-2 border border-bg/40 text-xs">Cancelar</button>
                                <button onClick={saveEdit} className="h-7 px-2 bg-bg text-forest text-xs font-semibold flex items-center gap-1" data-testid={`chat-edit-save-${m.id}`}>
                                  <Check size={12} weight="bold" /> Guardar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {m.attachment && <MessageAttachment attachment={m.attachment} mine={mine} />}
                              {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                              <div className={`text-[10px] font-mono flex items-center gap-1 ${mine ? "text-bg/60" : "text-inkMuted"}`}>
                                {formatTime(m.created_at)}
                                {m.edited_at && <span>· editado</span>}
                                {mine && !m.edited_at && <span>{m.read ? " · leído" : " · enviado"}</span>}
                              </div>
                            </>
                          )}
                        </div>
                        {mine && !isDeleted && !isEditingThis && (
                          <div className="flex flex-col gap-1 ml-1 opacity-0 group-hover:opacity-100 transition-opacity self-center">
                            <button
                              onClick={() => startEdit(m)}
                              className="p-1 border border-hairline bg-surface hover:bg-forest hover:text-bg"
                              title="Editar"
                              data-testid={`chat-msg-edit-${m.id}`}
                            >
                              <PencilSimple size={12} weight="bold" />
                            </button>
                            <button
                              onClick={() => deleteMsg(m)}
                              className="p-1 border border-danger bg-surface text-danger hover:bg-danger hover:text-bg"
                              title="Eliminar"
                              data-testid={`chat-msg-delete-${m.id}`}
                            >
                              <Trash size={12} weight="bold" />
                            </button>
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>

              <form onSubmit={send} className="border-t border-hairline p-3 flex flex-col gap-2 flex-shrink-0 relative" data-testid="chat-form">
                <QuickReplies
                  open={qrOpen}
                  query={qrQuery}
                  onPick={pickQuickReply}
                  onClose={() => setQrOpen(false)}
                />
                {pending && (
                  <PendingAttachment att={pending} onRemove={() => setPending(null)} />
                )}
                <div className="flex gap-2 items-end">
                  <AttachButton onFile={setPending} disabled={sending} />
                  <textarea
                    ref={inputRef}
                    data-testid="chat-input"
                    value={text}
                    onChange={onTextChange}
                    onKeyDown={(e) => {
                      if (e.key === "Escape" && qrOpen) {
                        setQrOpen(false);
                        return;
                      }
                      if (e.key === "Enter" && !e.shiftKey && !qrOpen) {
                        e.preventDefault();
                        send(e);
                      }
                    }}
                    rows={1}
                    placeholder={pending ? "Agrega un mensaje (opcional)…" : (user.role === "advisor" || user.role === "admin") ? "Escribe… (usa / para respuestas rápidas)" : "Escribe un mensaje…"}
                    className="flex-1 min-h-[48px] max-h-[180px] py-3 px-4 border border-hairline bg-surface focus:outline-none focus:border-forest resize-none font-body text-sm leading-normal whitespace-pre-wrap"
                    style={{ fontFamily: 'inherit' }}
                  />
                  <button data-testid="chat-send" disabled={sending} className="h-12 px-5 bg-forest text-bg font-semibold flex items-center gap-2 hover:bg-forest-hover disabled:opacity-60 flex-shrink-0">
                    <PaperPlaneTilt size={16} weight="bold" /> Enviar
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
      {active && partner?.role === "client" && clientId && (
        <ChatSimulator
          open={simOpen}
          onClose={() => setSimOpen(false)}
          clientName={partner?.name || "Cliente"}
          conversationId={active.id}
          onSent={(msg) => {
            setMessages((m) => [...m, msg]);
            setTimeout(() => {
              if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }, 50);
          }}
        />
      )}
    </div>
  );
}
